import { useLoaded } from '@interflora/ui-components/build/components/MainContent/useComponentLoading'
import AvailabilityCheckerUI, {
  Props,
} from '@interflora/ui-components/build/components/AvailabilityChecker/AvailabilityChecker'
import { SiteContext } from '@interflora/ui-components/build/utils/common'
import { useAnalytics } from 'hooks/useAnalytics'
import AvailabilityCheckerContext from 'context/AvailabilityCheckerContext'
import { useContext, useState } from 'react'
import useCountriesList from '../../hooks/useCountriesList'
import type { FormProps as PCCFormProps } from '@interflora/ui-components/build/components/PCCModal/PCCModal'

const AvailabilityChecker = (props: Props) => {
  const [showInternationalLink, setShowInternationalLink] = useState(false)
  const { isFlyingFlowers } = useContext(SiteContext)
  const analytics = useAnalytics()
  const { setFormValues } = useContext(AvailabilityCheckerContext) || {}

  // Fetch countries list only when needed
  const { countriesList } = useCountriesList({
    skip: isFlyingFlowers,
    onlyFetchWhen: showInternationalLink,
  })
  useLoaded(props)

  /**
   * IFCT-8570: Store form values in context when the availability checker is submitted.
   * Only write to context when skipRedirect is true (category page PCC) to avoid
   * side-effects on other pages that use the normal /search redirect flow.
   */
  const handleFormSubmit = (formValues: PCCFormProps) => {
    if (props.skipRedirect && setFormValues) {
      setFormValues(formValues)
    }
  }

  return (
    <AvailabilityCheckerUI
      countriesList={countriesList}
      backgroundColour={props.backgroundColour}
      clickPostCodeChecker={() => analytics.postCodeCheckerOpen()}
      trackPostCodeCheckerDate={(date?: string) => analytics.postCodeCheckerDate(date)}
      trackPostCodeCheckerAddress={(pcc_postcode: string, pcc_city: string, pcc_county: string) => {
        // IFCT-9214: category-page PCC (skipRedirect) reports completion via pcc_path only —
        // pcc_address_success stays on the redirect-based PCC flows (search page, header nav).
        if (props.skipRedirect) return
        analytics.postCodeCheckerAddressSuccess(pcc_postcode, pcc_city, pcc_county)
      }}
      trackPostCodeCheckerManualAddress={() => analytics.postCodeCheckerAddressManual()}
      trackPostCodeCheckerOccasion={(occasion: string) => analytics.postCodeCheckerOccasionSelected(occasion)}
      submitPostCodeCheckerComplete={() => {
        // IFCT-9214: see trackPostCodeCheckerAddress above — same category-page exclusion for pcc_complete.
        if (props.skipRedirect) return
        analytics.postCodeCheckerComplete()
      }}
      trackEnterManualAddressClick={(click_location) => analytics.trackEnterManualAddressClick(click_location)}
      locationInternationalDelivery={(click_location: string) => {
        analytics.doYouWantInternationalDelivery(click_location)
      }}
      occasionQuickLinks={props.occasionQuickLinks}
      pccEditFeatureTracking={(pcc_edit_location: string) => analytics.pccEditFeatureTracking(pcc_edit_location)}
      showInternationalLink={showInternationalLink}
      setShowInternationalLink={setShowInternationalLink}
      trackPCCCloseAnalytics={(location: string) => analytics.trackPostCodeCheckerClose(location)}
      trackPCCStartNewSearch={() => analytics.trackPCCStartNewSearch()}
      showRecommendedFacetsInPCC={props.showRecommendedFacetsInPCC}
      hideOccasion={props.hideOccasion}
      skipRedirect={props.skipRedirect}
      onAvailabilityCheckerSubmit={handleFormSubmit}
      defaultOpen={props.defaultOpen}
      isOpenControlled={props.isOpenControlled}
      setOpenControlled={props.setOpenControlled}
      initialStep={props.initialStep}
      initialInfo={props.initialInfo}
    />
  )
}

export default AvailabilityChecker
